/**
 * Widget cockpit — file des effets différés en attente.
 *
 * Affiche, sous forme de liste compacte, les retombées promises par les
 * décisions passées : chaque entrée porte le mois cible et la décision
 * d'origine. C'est le levier de rétention #1 — il transforme chaque
 * décision en pari à terme et donne au joueur une raison de revenir au
 * tour suivant.
 *
 * Source : `engine.pendingEffects()` (Rust → JSON via WASM), rafraîchi
 * par le store après chaque `apply_turn`.
 */

import { useState } from "react";
import { useGame } from "../store/useGame";
import type { PendingEffect } from "../engine/wasm";

const MONTHS = [
  "janv.",
  "févr.",
  "mars",
  "avril",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];

function formatTargetTurn(turn: number): string {
  // Cohérent avec MetaBar : 1 tour = 1 mois depuis janv. 2028.
  const totalMonths = turn;
  const m = totalMonths % 12;
  const y = 2028 + Math.floor(totalMonths / 12);
  return `${MONTHS[m]} ${y}`;
}

export function DelayedEffectsQueue() {
  const pendingEffects = useGame((s) => s.pendingEffects);
  const currentTurn = useGame((s) => s.worldState?.turn ?? 0);
  const [expanded, setExpanded] = useState(false);

  const upcoming = pendingEffects.filter((e) => e.target_turn > currentTurn);
  if (upcoming.length === 0) {
    return null;
  }

  // Regrouper par tour cible pour un affichage plus dense.
  const grouped = new Map<number, PendingEffect[]>();
  for (const e of upcoming) {
    const list = grouped.get(e.target_turn) ?? [];
    list.push(e);
    grouped.set(e.target_turn, list);
  }
  const groups = [...grouped.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <section className="delayed-effects" aria-label="Effets en attente">
      <button
        type="button"
        className="delayed-summary"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="delayed-label">EFFETS À VENIR</span>
        <span className="delayed-count">{upcoming.length}</span>
        <span className="delayed-toggle">{expanded ? "▾" : "▸"}</span>
      </button>

      {expanded && (
        <ol className="delayed-list">
          {groups.map(([turn, effects]) => {
            const distance = turn - currentTurn;
            return (
              <li key={turn} className="delayed-group">
                <div className="delayed-when">
                  <span className="when-month">{formatTargetTurn(turn)}</span>
                  <span className="when-distance">dans {distance} mois</span>
                </div>
                <ul className="delayed-items">
                  {effects.map((e, i) => (
                    <li key={i} className="delayed-item">
                      <span className="item-kicker">{e.archetype_kicker}</span>
                      {e.archetype_prompt && (
                        <span className="item-prompt">
                          {e.archetype_prompt}
                        </span>
                      )}
                      <span className="item-label">« {e.option_label} »</span>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
